import type { ImportedDocumentRenameFailedAction } from '@Application/Beneficiaries/ImportedDocuments/Actions/importedDocumentRenameFailed';
import RenameImportedDocument from '@Application/Beneficiaries/ImportedDocuments/Commands/RenameImportedDocument';
import renameImportedDocument from '@Application/Beneficiaries/ImportedDocuments/renameImportedDocument';
import type SubjectAddress from '@Domain/Subjects/SubjectAddress';
import EditableTitle from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Shared/EditableTitle';
import useStrictCurrentFacility from '@UI/Providers/CurrentFacilityProvider/useStrictCurrentFacility';
import * as React from 'react';
import { useDispatch } from '@verclam/synapse';

type Props = {
	readonly subject: SubjectAddress;
	readonly importedDocumentId: number;
	readonly title: string;
	/** The write right of the preparation: permission and modifiable dossier. */
	readonly canRename: boolean;
};

/** The title of an imported document, renamable in place. Leaf component: it dispatches the rename itself. */
const ImportedDocumentTitle: React.FC<Props> = ({ subject, importedDocumentId, title, canRename }) => {
	const dispatch = useDispatch();
	const { currentFacility } = useStrictCurrentFacility();
	const facilityId = currentFacility.id;

	const handleRename = React.useCallback(
		(name: string) =>
			dispatch(
				renameImportedDocument(
					new RenameImportedDocument(facilityId, subject, importedDocumentId, name),
				),
			).then(
				() => undefined,
				(failure: ImportedDocumentRenameFailedAction) => Promise.reject(failure.payload),
			),
		[dispatch, facilityId, subject, importedDocumentId],
	);

	return (
		<EditableTitle
			title={title}
			editKey={`imported-document-${importedDocumentId}`}
			canRename={canRename}
			fieldLabel="Nom du document"
			onRename={handleRename}
		/>
	);
};

export default ImportedDocumentTitle;
